import {
  type GetDetailedSimulationResponse,
  PriceStatus,
  PriceUnitType,
  type SimulationData,
  type SimulationFieldExtractionResult,
  type SimulationKeyFigure,
  type SimulationKeyFigureRequestItem,
  type SimulationKeyFigureValue,
  type SimulationMismatchRequestItem,
} from '@/api';
import { round } from '@/components/table/table-format-helpers';
import {
  formatCorridorMark,
  getPpcCorridor,
  mapSimulationDataToRowData,
} from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-helpers';
import type { SimulationIndexRow } from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-index-types';
import type {
  PricelistSettings,
  RowData,
} from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-types';

/** Values must match exactly once both sides are rounded to the field's precision. */
const COMPARISON_TOLERANCE = 0;

/**
 * The backend's `FormatDecimal("0.0000")` truncates **product-level** numeric fields to 4 decimal
 * places during extraction. The simulation stores full-precision floats. To align both sides
 * through the same precision pipeline, the sim value is first rounded to 4dp (matching what the
 * backend would emit) before being rounded to the field's display precision. Without this, values
 * like `14.694984` round directly to `14.69` at 2dp, while the file's `14.6950` (already 4dp)
 * rounds to `14.70` — a false mismatch caused by the intermediate truncation step the file went
 * through but the sim didn't.
 *
 * NOTE: This intermediate step applies only to product-level field comparisons (`valuesMatch` /
 * `buildFieldComparisons`). Key figures are extracted via `GetRawCellValue` at full precision, so
 * `buildKeyFigureRequestItem` rounds directly to the field's display precision instead.
 */
const BACKEND_EXTRACTION_DECIMALS = 4;

/**
 * Rounds to `decimals` places using the same decimal-accurate half-up rounding the Simulation Tool
 * UI uses (`round` in table-format-helpers, backed by Big.js). This matters at boundaries: the
 * binary float for `28.805` is `28.80499…`, so `Number(v.toFixed(2))` yields `28.80` while the UI
 * (and the workbook) show `28.81`. Aligning to the UI's rounding keeps the comparison consistent
 * with what a user sees on screen. Non-finite values are returned unchanged (Big throws on them).
 */
const roundToPrecision = (value: number, decimals: number): number =>
  Number.isFinite(value) ? round(value, decimals) : value;

export type FieldComparisonRow = {
  productId: number;
  productName: string;
  field: string;
  fileValue: string | null;
  simulationValue: string | null;
  isMatch: boolean;
};

const parseNumber = (value: string | null | undefined): number | null => {
  if (value === null || value === undefined || value.trim() === '') return null;
  // Workbook values use a comma decimal separator and may carry a trailing '%'.
  const normalized = value.trim().replace('%', '').replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Number of decimals used when a percentage field is compared in raw-fraction form. */
const RAW_FRACTION_DECIMALS = 3;
/** Number of decimals used when a percentage field is compared in percentage form. */
const PERCENTAGE_DECIMALS = 1;

/**
 * Whether the workbook wrote a percentage field in percentage form (e.g. `28,2%` or `25.85`)
 * rather than as a raw fraction (e.g. `0.2585`). The simulation always stores the raw fraction,
 * so this decides how to reconcile the two: a `%` sign, or a magnitude strictly greater than 1,
 * means the file is in percentage form. `> 1` (not `>= 1`) keeps an exact `1.0` raw fraction
 * (a 100% margin) classified as a fraction rather than as `1%`.
 */
const isPercentageForm = (raw: string, value: number): boolean =>
  raw.includes('%') || Math.abs(value) > 1;

/**
 * Compares a percentage field (RM%/VAT%) whose simulation value is always a raw fraction against a
 * workbook value that may be either a raw fraction (compared at 3 dp) or percentage form (compared
 * at 1 dp after scaling the simulation fraction ×100).
 */
const percentageValuesMatch = (
  fileRaw: string,
  fileNumber: number,
  simNumber: number,
): boolean => {
  // VALUE() pre-step: when both sides are numerically equal at backend extraction precision they
  // match regardless of form — skip form detection entirely. This prevents false mismatches when
  // calculate_rm() returns extreme values (e.g. −3073 when LP >> RRP) that isPercentageForm
  // would misclassify as percentage form and then erroneously scale the simulation value ×100.
  const preFile = roundToPrecision(fileNumber, BACKEND_EXTRACTION_DECIMALS);
  const preSim = roundToPrecision(simNumber, BACKEND_EXTRACTION_DECIMALS);
  if (Math.abs(preFile - preSim) <= COMPARISON_TOLERANCE) return true;

  if (isPercentageForm(fileRaw, fileNumber)) {
    // Sim is a raw fraction; scale ×100, then align through 4dp→target precision.
    const scaledSim = simNumber * 100;
    const alignedSim = roundToPrecision(
      roundToPrecision(scaledSim, BACKEND_EXTRACTION_DECIMALS),
      PERCENTAGE_DECIMALS,
    );
    return (
      Math.abs(
        roundToPrecision(fileNumber, PERCENTAGE_DECIMALS) - alignedSim,
      ) <= COMPARISON_TOLERANCE
    );
  }
  // Raw fraction form: align sim through 4dp→3dp.
  const alignedSim = roundToPrecision(
    roundToPrecision(simNumber, BACKEND_EXTRACTION_DECIMALS),
    RAW_FRACTION_DECIMALS,
  );
  return (
    Math.abs(
      roundToPrecision(fileNumber, RAW_FRACTION_DECIMALS) - alignedSim,
    ) <= COMPARISON_TOLERANCE
  );
};

/**
 * Compares a workbook value against a simulation value. When `decimals` is given the values are
 * treated as numeric and rounded to that precision first. `percent` fields (RM%/VAT%) instead use
 * form-aware comparison: raw fraction at 3 dp, or percentage form at 1 dp. When `decimals` is
 * omitted, or either side isn't numeric, the values are compared as case-insensitive strings. Both
 * sides empty counts as a match.
 */
const valuesMatch = (
  fileValue: string | null,
  simulationValue: string | null,
  decimals?: number,
  percent?: boolean,
): boolean => {
  const normalizedFile = fileValue?.trim() || null;
  const normalizedSimulation = simulationValue?.trim() || null;

  if (normalizedFile === null && normalizedSimulation === null) return true;
  if (normalizedFile === null || normalizedSimulation === null) return false;

  const fileNumber =
    decimals !== undefined ? parseNumber(normalizedFile) : null;
  const simNumber =
    decimals !== undefined ? parseNumber(normalizedSimulation) : null;

  if (decimals !== undefined && fileNumber !== null && simNumber !== null) {
    if (percent) {
      return percentageValuesMatch(normalizedFile, fileNumber, simNumber);
    }

    // Round sim to 4dp first (matching backend extraction precision), then to field precision.
    const alignedSim = roundToPrecision(
      roundToPrecision(simNumber, BACKEND_EXTRACTION_DECIMALS),
      decimals,
    );
    return (
      Math.abs(roundToPrecision(fileNumber, decimals) - alignedSim) <=
      COMPARISON_TOLERANCE
    );
  }

  return normalizedFile.toLowerCase() === normalizedSimulation.toLowerCase();
};

const formatNumber = (value: number | null | undefined): string =>
  String(value ?? 0);

/** Backend `FormatBool` emits `Yes`/`No`; match that for boolean field comparison. */
const formatBool = (value: boolean | null | undefined): string =>
  value ? 'Yes' : 'No';

const formatDate = (value: Date | null | undefined): string | null =>
  value
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
        value.getDate(),
      ).padStart(2, '0')}`
    : null;

/** Maps the product-level `priceStatus` to the workbook's `A`/`P` validity marker. */
const formatPriceValidity = (
  status: PriceStatus | null | undefined,
): string | null => {
  if (status === PriceStatus.Approved) return 'A';
  if (status === PriceStatus.Preliminary) return 'P';
  return null;
};

/** Normalizes the workbook's PPC result (`In`/`Below`/`Above`) to the corridor mark the simulation emits. */
const normalizePpcMark = (value: string | null): string | null => {
  if (value === null) return null;
  const trimmed = value.trim().toLowerCase();
  if (trimmed === 'above') return 'ABOVE';
  if (trimmed === 'below') return 'BELOW';
  if (trimmed === 'in') return '';
  return value;
};

/**
 * How to read one field off the simulation and how to compare it against the workbook value.
 * `read` pulls the value from the raw `SimulationData` row; `readRow` pulls it from the derived
 * `RowData` row (needed for values computed from price-list settings, e.g. indexes / FMC / NIP).
 * `decimals` is the rounding precision for numeric compares; `percent` fields (RM%/VAT%) use
 * form-aware comparison (raw fraction 3 dp, or percentage form 1 dp). Precision is omitted for
 * text/date/boolean fields compared as strings. `lead` fields are skipped for LEAD price units.
 */
type FieldSpec = {
  read?: (data: SimulationData) => string | null;
  readRow?: (row: RowData) => string | null;
  decimals?: number;
  /** Percentage field (RM%/VAT%): form-aware compare (raw fraction 3 dp, or percentage 1 dp). */
  percent?: boolean;
  lead?: boolean;
  /** Backend extraction truncates this field at the given length; both sides are truncated before comparison. */
  maxLength?: number;
  /** Normalize the file value before comparison (e.g. treat '#' as null for Product Audience). */
  normalizeFileValue?: (value: string | null) => string | null;
};

/**
 * Every field the workbook exposes that has a comparable counterpart on the created simulation.
 * Keys are the field names the backend's extraction emits (see
 * `SimulationExcelImportService.FieldSpecs`), verified against a live extraction — NOT the
 * `Comparison fields.xlsx` `LPPS column` labels. Fields the backend extracts but that have no
 * simulation counterpart are intentionally omitted, so they're never compared. Lead fields
 * (`lead: true`) are skipped entirely for LEAD price units.
 */
const FIELD_SPECS: Record<string, FieldSpec> = {
  // ── Master data (string / date / boolean, exact compare) ──────────────────
  Theme: { read: (d) => d.themeName ?? null },
  'Box Size': { read: (d) => d.boxSizeName ?? null },
  'Product Group': { read: (d) => d.productGroupName ?? null },
  'Product Category': {
    read: (d) => d.productCategoryName ?? null,
    maxLength: 20,
  },
  'Product Class Lvl1': { read: (d) => d.productClassLevelOneName ?? null },
  'Product Class Lvl2': { read: (d) => d.productClassLevelTwoName ?? null },
  'Product Audience': {
    read: (d) => d.productAudience ?? null,
    /** The workbook uses '#' as a placeholder for empty; normalize to null before comparing. */
    normalizeFileValue: (v) => (v === '#' ? null : v),
  },
  'Product Composition': { read: (d) => d.productCompositionName ?? null },
  'Local Launch Date': { read: (d) => formatDate(d.localLaunchDate) },
  'Local Exit Date': { read: (d) => formatDate(d.localExitDate) },
  'Development Ownership': { read: (d) => d.developmentOwnershipId ?? null },
  'License Mark': { read: (d) => formatBool(d.license) },

  // ── Local key figures (numeric) ───────────────────────────────────────────
  RRP: { read: (d) => formatNumber(d.rrp), decimals: 2 },
  'RM%': {
    read: (d) => formatNumber(d.rm),
    decimals: RAW_FRACTION_DECIMALS,
    percent: true,
  },
  'List Price': { read: (d) => formatNumber(d.lp), decimals: 2 },
  'SI Pcs': { read: (d) => formatNumber(d.forecastedDemand), decimals: 0 },

  // ── Local lead key figures (skipped for LEAD price units) ─────────────────
  // On a LEAD price unit the file can't reference the lead to itself, so these are 0 in the
  // workbook while the simulation still computes them — comparing would be a false mismatch.
  'Local Lead RRP': {
    read: (d) => formatNumber(d.localLeadRrp),
    decimals: 2,
    lead: true,
  },
  'Local Lead RM%': {
    read: (d) => formatNumber(d.localLeadRm),
    decimals: RAW_FRACTION_DECIMALS,
    percent: true,
    lead: true,
  },
  'Local Lead List Price': {
    read: (d) => formatNumber(d.localLeadLp),
    decimals: 2,
    lead: true,
  },

  // ── Derived key figures (need price-list settings, read off RowData) ──────
  'RRP Index': {
    readRow: (r) => formatNumber(r.rrpIndexLocalLead),
    decimals: 0,
    lead: true,
  },
  'List Price Index': {
    readRow: (r) => formatNumber(r.lpIndexLocalLead),
    decimals: 0,
    lead: true,
  },
  'List Price/FMC Factor': {
    readRow: (r) => formatNumber(r.listPriceFmcFactor),
    decimals: 3,
  },
  'Local Sim FMC': { readRow: (r) => formatNumber(r.fmcLocal), decimals: 2 },

  // ── Dormant: spec'd for #382 but not yet emitted by the backend extraction. ─
  // Keys/readers are ready so they activate automatically once the backend sends them; until
  // then the field simply never appears in `product.fields` and is skipped.
  'VAT %': {
    read: (d) => formatNumber(d.vat),
    decimals: RAW_FRACTION_DECIMALS,
    percent: true,
  },
  NIP: { readRow: (r) => formatNumber(r.nip), decimals: 2 },
  'LL NIP': { readRow: (r) => formatNumber(r.llNip), decimals: 2, lead: true },
  'Price Validity': { read: (d) => formatPriceValidity(d.priceStatus) },
};

/** Field name used for the single mismatch row created when a product is entirely missing from either side of the comparison, instead of one row per field. */
const MISSING_PRODUCT_FIELD = 'ALL';

/**
 * Compares every field extracted from the uploaded workbook (RRP/RM%/List Price plus the other
 * fields the backend extracts — Theme, Box Size, Local Lead values, dates, etc.) against the
 * values persisted on the newly created simulation, matching products by their productId. If a
 * product is present in the file but entirely missing from the simulation (or vice versa), a
 * single `ALL` field mismatch row is created for that product instead of one row per field.
 */
export const buildFieldComparisons = (
  extraction: SimulationFieldExtractionResult,
  simulation: GetDetailedSimulationResponse,
  pricelistSettings: Map<string, PricelistSettings>,
  priceUnitType: PriceUnitType | undefined,
  totalLlRrpIndex: Map<string, number>,
): FieldComparisonRow[] => {
  const isLead = priceUnitType === PriceUnitType.Lead;

  const simulationByProductId = new Map(
    simulation.simulationData
      .filter((row) => row.productId !== undefined)
      .map((row) => [row.productId as number, row]),
  );

  // Some fields (e.g. LL RRP/LP Index, LP/FMC Factor, NIP) aren't stored directly on
  // `SimulationData` but are derived per-product the same way the Simulation Tool page derives
  // its `RowData` columns, which requires the price list's settings (alt FX rate, discounts).
  const rowDataByProductId = new Map(
    mapSimulationDataToRowData(simulation.simulationData, pricelistSettings)
      .filter((row) => row.productId !== undefined)
      .map((row) => [row.productId, row]),
  );

  const extractionProductIds = new Set<number>();
  const rows: FieldComparisonRow[] = [];

  for (const product of extraction.products ?? []) {
    if (product.productId === undefined) continue;
    extractionProductIds.add(product.productId);

    const simulationRow = simulationByProductId.get(product.productId);
    const productName = product.productName ?? simulationRow?.productName ?? '';

    if (!simulationRow) {
      rows.push({
        productId: product.productId,
        productName,
        field: MISSING_PRODUCT_FIELD,
        fileValue: 'Product present in file',
        simulationValue: 'Product missing in simulation',
        isMatch: false,
      });
      continue;
    }

    const rowData = rowDataByProductId.get(product.productId);

    for (const field of Object.keys(product.fields ?? {})) {
      const spec = FIELD_SPECS[field];
      if (!spec) continue;
      // The Local Lead fields have no meaningful counterpart on a LEAD price unit.
      if (isLead && spec.lead) continue;

      const simulationValue = spec.read
        ? spec.read(simulationRow)
        : spec.readRow && rowData
          ? spec.readRow(rowData)
          : undefined;
      // A row-derived field can't be compared without its computed `RowData`.
      if (simulationValue === undefined) continue;

      const fileValue = product.fields?.[field] ?? null;
      // Normalize for comparison only (e.g. '#' → null for Product Audience);
      // the raw file value is preserved in the mismatch row for transparency.
      const normalizedFileValue = spec.normalizeFileValue
        ? spec.normalizeFileValue(fileValue)
        : fileValue;

      // When the backend truncates a text field at `maxLength`, truncate the simulation side
      // to the same length before comparing so the limit doesn't produce a false mismatch.
      const compareFileValue =
        spec.maxLength && normalizedFileValue
          ? normalizedFileValue.slice(0, spec.maxLength)
          : normalizedFileValue;
      const compareSimValue =
        spec.maxLength && simulationValue
          ? simulationValue.slice(0, spec.maxLength)
          : simulationValue;

      rows.push({
        productId: product.productId,
        productName,
        field,
        fileValue,
        simulationValue,
        isMatch: valuesMatch(
          compareFileValue,
          compareSimValue,
          spec.decimals,
          spec.percent,
        ),
      });
    }

    // PPC mark isn't extracted as a plain field on the simulation — it's derived per product from
    // the corridor thresholds and the portfolio-level LL RRP index. That index is self-referential
    // (0) on a LEAD price unit, so — like the other Local-Lead-derived fields — PPC is skipped for
    // LEAD to avoid a meaningless comparison.
    const ppcFileValue = product.fields?.['PPC Mark'];
    if (!isLead && ppcFileValue !== undefined && rowData) {
      const simulationValue = formatCorridorMark(
        getPpcCorridor(rowData, pricelistSettings, totalLlRrpIndex),
      );
      const fileValue = normalizePpcMark(ppcFileValue);
      rows.push({
        productId: product.productId,
        productName,
        field: 'PPC Mark',
        fileValue,
        simulationValue,
        isMatch: (fileValue?.trim() ?? '') === (simulationValue?.trim() ?? ''),
      });
    }
  }

  // Products persisted on the simulation but entirely missing from the uploaded file.
  for (const [productId, simulationRow] of simulationByProductId) {
    if (extractionProductIds.has(productId)) continue;

    rows.push({
      productId,
      productName: simulationRow.productName ?? '',
      field: MISSING_PRODUCT_FIELD,
      fileValue: 'Product missing in file',
      simulationValue: 'Product present in simulation',
      isMatch: false,
    });
  }

  return rows;
};

/**
 * Formats a date as `ddmmyyyy` (e.g. `17082026`), matching the format the backend expects for
 * the `prefix` query parameter used to filter excel-import files/mismatches by date.
 */
export const formatDateAsDdMmYyyyPrefix = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}${month}${year}`;
};

/**
 * Converts the mismatched rows of a field comparison into the request items expected by
 * `POST /simulation/excel-import/mismatches`, so the backend can persist a record of every
 * field that didn't match between the uploaded workbook and the simulation it was compared to.
 * Rows that matched are excluded — only mismatches are reported.
 */
export const buildMismatchRequestItems = (
  comparisons: FieldComparisonRow[],
  priceListId: number,
  priceListName: string,
  year: number,
): SimulationMismatchRequestItem[] =>
  comparisons
    .filter((row) => !row.isMatch)
    .map((row) => ({
      priceListId,
      priceList: priceListName,
      year,
      product: row.productName,
      field: row.field,
      fileValue: row.fileValue,
      simulationValue: row.simulationValue,
    }));

/**
 * Generates a simulation name from the uploaded file name that satisfies
 * `validateSimulationName` (no special characters such as `.`, `/`, `:`, etc.).
 */
export const generateSimulationNameFromFile = (fileName: string): string => {
  const baseName = fileName.replace(/\.[^/.]+$/, '');
  const sanitizedBaseName = baseName
    .replace(/[!@#$%^&*()+={}[\]|\\:;"'<>,.?/~`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const now = new Date();
  const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}-${String(
    now.getMinutes(),
  ).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;

  const name = sanitizedBaseName
    ? `${sanitizedBaseName} ${timestamp}`
    : `Uploaded Simulation ${timestamp}`;

  return name.slice(0, 100);
};

/**
 * Maps a workbook "Keyfigures" GRAND TOTAL row name to the corresponding field on the
 * portfolio-level `SimulationIndexRow` computed by `computeSimulationIndexes`, and the number
 * of decimal places to round both values to before comparing them. Index-based fields
 * (RRP/List Price/NIP indexes) compare with 0 decimals; Sales Revenue with 0; percentage figures
 * (Retail Margin %) are stored as a raw fraction in the workbook (e.g.
 * `0.219`) while the index computes them as a 0-100 percentage (e.g. `21.9`), so the file value is
 * scaled ×100 (`percent: true`) before both are compared in percentage form at 1 decimal.
 */
const KEY_FIGURE_FIELD_MAP: Record<
  string,
  {
    field: keyof SimulationIndexRow;
    decimals: number;
    percent?: boolean;
    /** Skip this key figure when the price unit type is LEAD (self-referential indexes are 0 in the file vs 100 in the sim). */
    lead?: boolean;
  }
> = {
  'Sales Revenue': { field: 'salesRevenue', decimals: 0 },
  'List Price/FMC Factor': { field: 'listPriceFmcFactor', decimals: 3 },
  'LL List Price Index': { field: 'lpLocalLeadIndex', decimals: 0, lead: true },
  'LL NIP Index': { field: 'nipLocalLeadIndex', decimals: 0, lead: true },
  'GL NIP Index': { field: 'nipGlobalLeadIndex', decimals: 0, lead: true },
  'Retail Margin %': { field: 'rm', decimals: 1, percent: true },
  'LL RRP Index': { field: 'rrpIndex', decimals: 0, lead: true },
};

/** Matches the workbook's key figure name against the mapping, ignoring a trailing currency suffix (e.g. "Sales Revenue (DKK)"). */
const resolveKeyFigureMapping = (name: string) => {
  const trimmedName = name.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return KEY_FIGURE_FIELD_MAP[trimmedName];
};

/** Values must match exactly, with no tolerance for numeric differences. */
const KEY_FIGURE_COMPARISON_TOLERANCE = 0;

/** The `SimulationKeyFigureRequestItem` fields the workbook's key figures can be mapped to (everything but `priceListId`/`priceList`/`year`). */
type KeyFigureRequestField = Exclude<
  keyof SimulationKeyFigureRequestItem,
  'priceListId' | 'priceList' | 'year'
>;

/**
 * Builds the `keyFigures` item expected by `POST /simulation/excel-import/mismatches`, pairing
 * each *mismatched* workbook "Keyfigures" GRAND TOTAL value with the corresponding value from the
 * portfolio-level index row computed for the price unit the workbook was extracted for (using the
 * `KEY_FIGURE_FIELD_MAP` mapping/scaling/decimal precision). Fields that
 * match are omitted, and `null` is returned when none of the workbook's key figures mismatch, so
 * callers can skip creating a row for that price list entirely.
 */
export const buildKeyFigureRequestItem = (
  keyFigures: SimulationKeyFigure[] | null | undefined,
  indexRow: SimulationIndexRow | undefined,
  priceListId: number,
  priceListName: string,
  year: number,
  priceUnitType?: PriceUnitType,
): SimulationKeyFigureRequestItem | null => {
  const isLead = priceUnitType === PriceUnitType.Lead;
  const item: SimulationKeyFigureRequestItem = {
    priceListId,
    priceList: priceListName,
    year,
  };
  let hasMismatch = false;

  for (const keyFigure of keyFigures ?? []) {
    if (!keyFigure.name) continue;

    const mapping = resolveKeyFigureMapping(keyFigure.name);
    if (!mapping) continue;
    // LL indexes are self-referential on a LEAD price unit (file=0, sim=100) — skip them.
    if (isLead && mapping.lead) continue;

    const parsedFileValue = parseNumber(keyFigure.simulationYearValue);
    // Percentage figures are stored as a raw fraction in the workbook; scale to the 0-100
    // percentage form the index computes so both sides are compared on the same scale.
    const fileValue =
      parsedFileValue !== null && mapping.percent
        ? parsedFileValue * 100
        : parsedFileValue;
    const rawSimulationValue = indexRow?.[mapping.field];
    const simulationValue =
      typeof rawSimulationValue === 'number' ? rawSimulationValue : null;

    const roundedFileValue =
      fileValue !== null ? roundToPrecision(fileValue, mapping.decimals) : null;
    // Key figures are extracted via GetRawCellValue (full precision, no 4dp
    // truncation), unlike product-level fields which go through FormatDecimal
    // ("0.0000"). Rounding the sim value through 4dp→3dp here caused
    // double-rounding errors (e.g. 4.31149→4.3115→4.312 instead of 4.311).
    // Round directly to the field's display precision to match both the file
    // value and what the Simulation Tool UI shows.
    const roundedSimulationValue =
      simulationValue !== null
        ? roundToPrecision(simulationValue, mapping.decimals)
        : null;

    const isMatch =
      roundedFileValue !== null && roundedSimulationValue !== null
        ? Math.abs(roundedFileValue - roundedSimulationValue) <=
          KEY_FIGURE_COMPARISON_TOLERANCE
        : roundedFileValue === roundedSimulationValue;

    if (isMatch) continue;

    (item as Record<KeyFigureRequestField, SimulationKeyFigureValue>)[
      mapping.field as KeyFigureRequestField
    ] = {
      fileValue: roundedFileValue,
      simulationValue: roundedSimulationValue,
    };
    hasMismatch = true;
  }

  return hasMismatch ? item : null;
};
